import React, { useState } from 'react';

const CollisionDebugPopup = ({ isOpen, onClose, collisionData, onRunDebug, onClearDebug, onRepositionCollisions }) => {
  const [isMinimized, setIsMinimized] = useState(false);

  if (!isOpen) return null;

  const handleRunDebug = () => {
    onRunDebug();
  };

  const handleClearDebug = () => {
    onClearDebug();
  };

  const handleRepositionCollisions = () => {
    if (onRepositionCollisions) {
      onRepositionCollisions();
    }
  };

  return (
    <div 
      className="fixed top-4 right-4 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-w-md"
      style={{ minWidth: '300px' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3 bg-red-50 border-b border-gray-200 rounded-t-lg">
        <h3 className="text-sm font-semibold text-red-800">
          🔍 Collision Debug Tool
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="text-gray-500 hover:text-gray-700 text-xs px-2 py-1 rounded"
            title={isMinimized ? "Expand" : "Minimize"}
          >
            {isMinimized ? '▲' : '▼'}
          </button>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 font-bold text-lg leading-none"
            title="Close"
          >
            ×
          </button>
        </div>
      </div>

      {/* Content */}
      {!isMinimized && (
        <div className="p-4">
          {/* Control Buttons */}
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={handleRunDebug}
              className="px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 transition-colors"
            >
              🔍 Run Collision Check
            </button>
            <button
              onClick={handleClearDebug}
              className="px-3 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600 transition-colors"
            >
              🧹 Clear Boxes
            </button>
            {collisionData && collisionData.collisions && collisionData.collisions.length > 0 && (
              <button
                onClick={handleRepositionCollisions}
                className="px-3 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition-colors"
              >
                🔧 Fix Collisions
              </button>
            )}
          </div>

          {/* Results */}
          {collisionData && (
            <div className="space-y-3">
              {/* Summary */}
              <div className="p-2 bg-gray-50 rounded text-xs">
                <div className="font-medium text-gray-700">Summary:</div>
                <div className="text-gray-600">
                  📊 Total Labels: {collisionData.totalLabels}
                </div>
                <div className="text-gray-600">
                  ⚠️ Collisions: {collisionData.collisions?.length || 0}
                </div>
                <div className="text-gray-600">
                  🏷️ Labels Involved: {collisionData.collidingLabels?.length || 0}
                </div>
                {collisionData.zoom && (
                  <div className="text-gray-600">
                    🔍 Zoom Level: {collisionData.zoom}
                  </div>
                )}
                {collisionData.scale && (
                  <div className="text-gray-600 text-xs">
                    📏 Scale: 1:{collisionData.scale}
                  </div>
                )}
              </div>

              {/* Status Message */}
              <div className={`p-2 rounded text-xs ${
                collisionData.collisions?.length > 0 
                  ? 'bg-red-50 text-red-700 border border-red-200' 
                  : 'bg-green-50 text-green-700 border border-green-200'
              }`}>
                {collisionData.message}
              </div>

              {/* Collision Details */}
              {collisionData.collisions && collisionData.collisions.length > 0 && (
                <div className="space-y-2">
                  <div className="font-medium text-xs text-gray-700">Collision Details:</div>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {collisionData.collisions.map((collision, index) => (
                      <div key={index} className="p-2 bg-red-50 rounded text-xs border border-red-200">
                        <div className="font-medium text-red-800">
                          Collision #{index + 1}
                        </div>
                        <div className="text-red-700 mt-1">
                          <div>📍 "{collision.labelA}"</div>
                          <div className="text-center text-red-400">↕️</div>
                          <div>📍 "{collision.labelB}"</div>
                          <div className="text-red-600 text-xs mt-1">
                            Overlap: {collision.overlapArea?.toFixed(0)} px²
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Colliding Labels List */}
              {collisionData.collidingLabels && collisionData.collidingLabels.length > 0 && (
                <div className="space-y-2">
                  <div className="font-medium text-xs text-gray-700">
                    Labels with Bounding Boxes Shown:
                  </div>
                  <div className="max-h-20 overflow-y-auto">
                    <div className="flex flex-wrap gap-1">
                      {collisionData.collidingLabels.map((labelId, index) => (
                        <span 
                          key={index} 
                          className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs border border-orange-200"
                        >
                          {labelId}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Help Text */}
          <div className="mt-4 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
            <div className="font-medium mb-1">How to use:</div>
            <ul className="space-y-1 text-xs">
              <li>• Click "Run Collision Check" to detect overlapping labels</li>
              <li>• Red bounding boxes will appear around colliding labels</li>
              <li>• Click "Fix Collisions" to automatically reposition overlapping labels</li>
              <li>• Use "Clear Boxes" to remove all bounding boxes</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollisionDebugPopup;